import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { ScreenState } from "../../components/ScreenState";
import { OnboardingLayout } from "./OnboardingLayout";
import { useAsync } from "../../lib/useAsync";
import { fetchSubjects, saveOnboarding, type CatalogSubject } from "../../api/onboarding";
import { ApiError } from "../../api/client";
import { subjectsToPickForExam } from "../../lib/catalog";
import type { ExamChoice } from "../../lib/catalog";
import { setActiveExam } from "../../lib/activeExam";

interface SubjectPickState {
  exam?: ExamChoice;
  /** Chose both at signup: this exam's subjects first, then this one's. */
  next?: ExamChoice;
  /** Added from Profile, not signing up: back to the app afterwards. */
  adding?: boolean;
}

/**
 * Screen 8 — English is locked in, three more to pick.
 *
 * The button reads "Start practicing" rather than "Continue" because the next
 * thing is a live question, and it enables only at the exact count: a student
 * who picks two and leaves would land in a syllabus that cannot be scored
 * against four subjects.
 *
 * Two things were wrong here, and together they made registration end in
 * nothing. The chips came from a hardcoded list of twelve names whose ids
 * ("chemistry", "use-of-english") match nothing in the question bank, and the
 * button navigated without saving. So a student finished onboarding with an
 * empty subject list: Home had no cards, readiness had nothing to compute
 * against, and the mock had no subjects to draw from.
 */
export function SubjectPickScreen() {
  const navigate = useNavigate();
  const { exam, next, adding } = (useLocation().state ?? {}) as SubjectPickState;

  const catalog = useAsync(() => fetchSubjects(exam), [exam]);
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const subjects: CatalogSubject[] = catalog.data?.subjects ?? [];

  /*
   * English is mandatory for both exams (HR spec, screen 8). The backend marks
   * it via `is_mandatory`, but if the migration hasn't run yet the field comes
   * back false for every subject. Rather than showing English as a normal chip
   * a student can skip, fall back: treat any subject whose name is "English" or
   * "Use of English" as required regardless of the flag.
   */
  const isEnglish = (name: string) => /^(use of )?english$/i.test(name.trim());
  const required = subjects.filter(
    (subject) => subject.required || (!subjects.some((s) => s.required) && isEnglish(subject.name)),
  );
  const optional = subjects.filter((subject) => !required.includes(subject));

  // JAMB: exactly 3 optional. WAEC: unlimited.
  const maxPick = subjectsToPickForExam(exam);
  const unlimited = maxPick === null;

  const toggle = (id: string) => {
    setPicked((current) =>
      current.includes(id)
        ? current.filter((subject) => subject !== id)
        : !unlimited && current.length >= maxPick
          ? current
          : [...current, id],
    );
  };

  const complete = unlimited ? picked.length >= 1 : picked.length === maxPick;

  const start = async () => {
    if (!complete || saving) return;
    setSaving(true);
    setFailure(null);

    try {
      // The required subjects go with them: English is on the list for
      // everyone, and leaving it out would mean readiness ignored the one
      // subject every candidate sits.
      await saveOnboarding({
        subjectIds: [...required.map((subject) => subject.id), ...picked],
        completed: true,
        // Saved into this exam's profile only; the other keeps its own list.
        ...(exam ? { profile: exam } : {}),
      });
      if (next) {
        // Picked both: the second exam's subjects, on the same screen.
        setPicked([]);
        void navigate("/subjects", { state: { exam: next } });
        return;
      }
      if (exam) setActiveExam(exam);
      void navigate(adding ? "/home" : "/practice");
    } catch (caught) {
      setFailure(
        caught instanceof ApiError ? caught.message : "We couldn't save your subjects. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScreenState loading={catalog.loading} error={catalog.error} onRetry={catalog.reload}>
      <OnboardingLayout
        step={5}
        title={next || adding ? `Your ${exam} subjects` : "What are you focusing on?"}
        subtitle={
          unlimited
            ? `${required.map((subject) => subject.name).join(" and ") || "English"} is in for everyone. Pick as many as you're sitting — you can change this anytime.`
            : `${required.map((subject) => subject.name).join(" and ") || "English"} is in for everyone. Pick ${maxPick} more — you can change this anytime.`
        }
        action={
          <div className="flex flex-col gap-3">
            <p className="tabular m-0 text-center text-[12.5px] text-text-muted">
              {unlimited ? `${picked.length} picked` : `${picked.length} of ${maxPick} picked`}
            </p>
            {failure ? (
              <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
                {failure}
              </p>
            ) : null}
            <Button disabled={!complete || saving} onClick={() => void start()}>
              {saving ? "Loading your first question…" : "Start practicing"}
            </Button>
          </div>
        }
      >
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {required.map((subject) => (
            <li key={subject.id}>
              <span className="flex items-center gap-2 rounded-[var(--radius-chip)] border border-accent bg-accent-tint px-3.5 py-2.5 text-[13.5px] font-semibold">
                {subject.name}
                <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                  Required
                </span>
              </span>
            </li>
          ))}

          {optional.map((subject) => {
            const isPicked = picked.includes(subject.id);
            const atLimit = !unlimited && !isPicked && picked.length >= maxPick;

            return (
              <li key={subject.id}>
                <button
                  type="button"
                  aria-pressed={isPicked}
                  // Disabled at the limit rather than silently ignoring the tap —
                  // a chip that does nothing when pressed reads as broken.
                  disabled={atLimit || saving}
                  onClick={() => toggle(subject.id)}
                  className={`min-h-11 rounded-[var(--radius-chip)] border px-3.5 py-2.5 text-[13.5px] transition-colors disabled:opacity-40
                    ${isPicked ? "border-accent bg-accent-tint font-semibold text-text" : "border-border bg-surface text-text-muted"}`}
                >
                  {subject.name}
                </button>
              </li>
            );
          })}
        </ul>
      </OnboardingLayout>
    </ScreenState>
  );
}
